const appBuilderBaseUrlV1 = `/v1/appbuilder/spaces`
const appBuilderBaseUrlV2 = `/v2/appbuilder/spaces`
const appBuilderBaseUrlV3 = `/v3/appbuilder/spaces`
const getBaseUrl = (isV2 = false) => (isV2 ? appBuilderBaseUrlV2 : appBuilderBaseUrlV1)

/**
 * Get the actual url for fetching apps in Workshop
 *
 * Note that this uses the default query found in `index.tsx` (at the root)
 */
export const appsQueryOptions = ({ spaceId }: { spaceId: string }) => {
  return { queryKey: [`${appBuilderBaseUrlV1}/${spaceId}/instances`] }
}

export const getTemplateQueryOptions = ({ templateId }: { templateId: string }) => {
  return { queryKey: [`/v1/appbuilder/templates/${templateId}`] }
}

/**
 * Get the actual url for fetching an app in Workshop
 *
 * Note that this uses the default query found in `index.tsx` (at the root)
 */
export const getFetchAppQueryOptions = ({
  spaceId,
  appId,
  includeWorkflow = false
}: {
  spaceId: string
  appId: string
  includeWorkflow?: boolean
}) => {
  if (includeWorkflow) {
    return {
      queryKey: [`${appBuilderBaseUrlV1}/${spaceId}/instances/${appId}?includeFeatureWorkflows=${includeWorkflow}`]
    }
  } else {
    return { queryKey: [`${appBuilderBaseUrlV1}/${spaceId}/instances/${appId}`] }
  }
}

/**
 * Get the actual url for sharing an app in Workshop
 *
 * Note that this uses the default query found in `index.tsx` (at the root)
 */
export const appShareQueryOptions = ({ spaceId, appId }: { spaceId: string; appId: string }) => {
  return { queryKey: [`${appBuilderBaseUrlV1}/${spaceId}/instances/${appId}/share`] }
}

/**
 * Get the actual url for fetching a datasource configuration in Workshop
 */
export const getDataSourceConfigurationQueryOptions = ({
  spaceId,
  dataSourceId
}: {
  spaceId: string
  dataSourceId: string
}) => {
  return { queryKey: [`${appBuilderBaseUrlV3}/${spaceId}/datasources/${dataSourceId}`] }
}

/**
 * Used to create a feature
 */
export const featureQueryOptions = ({
  spaceId,
  appId,
  featureId,
  isV2 = false
}: {
  spaceId: string
  appId: string
  featureId?: string | null
  isV2?: boolean
}) => {
  if (featureId) {
    return { queryKey: [`${getBaseUrl(isV2)}/${spaceId}/instances/${appId}/features/${featureId}`] }
  } else {
    return { queryKey: [`${getBaseUrl(isV2)}/${spaceId}/instances/${appId}/features`] }
  }
}

/**
 * Used for v3 data source with an app operations
 */
export const datasourceV3WithAppQueryOptions = ({
  spaceId,
  appId,
  dataSourceId,
  datasourceInstanceId
}: {
  spaceId: string
  appId: string
  datasourceInstanceId: string
  dataSourceId: string
}) => {
  return {
    queryKey: [
      `${appBuilderBaseUrlV3}/${spaceId}/application/${appId}/datasources/${dataSourceId}/instances/${datasourceInstanceId}`
    ]
  }
}

/**
 * Used to replace a v3 app instance datasource
 */
export const replaceV3DataSourceWithAppQueryOptions = ({
  spaceId,
  appId,
  dataSourceId,
  datasourceInstanceId
}: {
  spaceId: string
  appId: string
  datasourceInstanceId: string
  dataSourceId: string
}) => {
  return {
    queryKey: [
      `${appBuilderBaseUrlV3}/${spaceId}/application/${appId}/datasources/${dataSourceId}/instances/${datasourceInstanceId}/replace`
    ]
  }
}

/**
 * Used get data source catalog
 *
 * @example
 * const { isLoading, isError, data } = useQuery(dataSourceV3Query({ spaceId, dataSourceId }))
 */
export const dataSourceV3CatalogQueryOptions = ({
  spaceId,
  datasourceId
}: {
  spaceId: string
  datasourceId?: string
}) => {
  if (datasourceId) {
    return { queryKey: [`${appBuilderBaseUrlV3}/${spaceId}/datasources/${datasourceId}`] }
  } else {
    return { queryKey: [`${appBuilderBaseUrlV3}/${spaceId}/datasources`] }
  }
}

/**
 * Used to update and create a dataSource with v3 api
 */
export const dataSourceV3QueryOptions = ({
  spaceId,
  datasourceId,
  instanceId
}: {
  spaceId: string
  datasourceId?: string
  instanceId?: string
}) => {
  if (instanceId) {
    return { queryKey: [`${appBuilderBaseUrlV3}/${spaceId}/datasources/${datasourceId}/instances/${instanceId}`] }
  } else if (datasourceId) {
    return { queryKey: [`${appBuilderBaseUrlV3}/${spaceId}/datasources/${datasourceId}/instances`] }
  } else {
    return { queryKey: [`${appBuilderBaseUrlV3}/${spaceId}/datasources/instances`] }
  }
}

export const dataSourceUserInstanceV3QueryOptions = ({
  spaceId,
  datasourceId,
  instanceId
}: {
  spaceId: string
  datasourceId: string
  instanceId: string
}) => {
  return { queryKey: [`${appBuilderBaseUrlV3}/${spaceId}/datasources/${datasourceId}/user/instances/${instanceId}`] }
}

/**
 * Used to update a dataSources members with v3 api
 */
export const dataSourceV3MembersQueryOptions = ({
  spaceId,
  datasourceId,
  instanceId
}: {
  spaceId: string
  datasourceId: string
  instanceId: string
}) => {
  return {
    queryKey: [`${appBuilderBaseUrlV3}/${spaceId}/datasources/${datasourceId}/instances/${instanceId}/members`]
  }
}

/**
 * Used to make a dataSource instance request with v3 api
 */
export const dataSourceV3RequestQueryOptions = ({
  spaceId,
  datasourceId,
  instanceId
}: {
  spaceId: string
  datasourceId?: string
  instanceId?: string
}) => {
  return {
    queryKey: [`${appBuilderBaseUrlV3}/${spaceId}/datasources/${datasourceId}/instances/${instanceId}/requests`]
  }
}

/**
 * Used to fetch apps used in a dataSource
 */
export const getDataSourceAppsQueryOptions = ({ spaceId, dataSourceId }: { spaceId: string; dataSourceId: string }) => {
  return { queryKey: [`${appBuilderBaseUrlV1}/${spaceId}/datasources/${dataSourceId}/apps`] }
}

/**
 * Used to connect datasource authsession
 */

export const dataSourceConnectQueryOptions = () => {
  return {
    queryKey: [`/v1/appbuilder/datasources/workgrid/connect`]
  }
}

/**
 * Used to authorize a datasource oauth flow
 */
export const dataSourceAuthorizeQueryOptions = ({
  spaceId,
  datasourceId
}: {
  spaceId: string
  datasourceId: string
}) => {
  return {
    queryKey: [`${appBuilderBaseUrlV3}/${spaceId}/datasources/${datasourceId}/oauth/authorize`]
  }
}

/**
 * Get the actual url for fetching the run history for a feature by ID
 */
export const getFetchHistoryQueryOptions = ({
  spaceId,
  appId,
  featureId,
  from
}: {
  spaceId: string
  appId: string
  featureId: string
  from: string
}) => {
  return {
    queryKey: [`${featureQueryOptions({ spaceId, appId, featureId, isV2: true }).queryKey[0]}/history?from=${from}`]
  }
}

/**
 * Get the url for a detailed run history for a specific execution history ID (get that from the `fetchHistory` call)
 */
export const getFetchFeatureExecutionHistoryQueryOptions = ({
  spaceId,
  appId,
  featureId,
  executionHistoryId,
  includeStepValues = true,
  executionDate
}: {
  spaceId: string
  appId: string
  featureId: string
  executionHistoryId: string
  includeStepValues?: boolean
  executionDate?: string
}) => {
  return {
    queryKey: [
      `${
        featureQueryOptions({
          spaceId,
          appId,
          featureId,
          isV2: true
        }).queryKey[0]
      }/execute/${executionHistoryId}/${executionDate}?includeStepValues=${includeStepValues}`
    ]
  }
}

/**
 * Get the url for the documentation endpoint to get all of the actions the polling event type emits
 */
export const getFetchPollingDiscoverQueryOptions = ({
  spaceId,
  appId,
  dataSourceId
}: {
  spaceId: string
  appId: string
  dataSourceId: string
}) => {
  return { queryKey: [`${appBuilderBaseUrlV1}/${spaceId}/instances/${appId}/datasources/${dataSourceId}/docs`] }
}

/**
 * Used for API Connector Credentials
 */
export const apiConnectorCredentialQueryOptions = ({
  spaceId,
  instanceId
}: {
  spaceId: string | null
  instanceId: string | null
}) => {
  return { queryKey: [`${appBuilderBaseUrlV1}/${spaceId}/instances/${instanceId}/apiconnector`] }
}

/**
 * Used for fetching Connector Logs
 */
export const fetchConnectorLogsQueryOptions = ({
  spaceId,
  connectorId,
  offset,
  startDate,
  endDate,
  correlationId
}: {
  spaceId: string | null
  connectorId: string | null
  offset: number | null
  startDate: string | null
  endDate: string | null
  correlationId?: string
}) => {
  const baseRequest = `/v1/audit/app/notification/logs?spaceId=${spaceId}&connectorId=${connectorId}&offset=${offset}&start=${startDate}&end=${endDate}`
  if (correlationId) {
    return { queryKey: [`${baseRequest}&correlationId=${correlationId}`] }
  }
  return { queryKey: [baseRequest] }
}
